import { Component, computed, effect, inject, input, output, signal, untracked } from '@angular/core';
import { FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ICampaignCreatorForm, ICtaForm } from '@campaign-form/campaign-form.interface';
import { ICtaTemplateApi } from '@campaign-form/form-fields/creators-and-ctas/cta-dialog.interface';
import { CreatorCustomValueCheckService } from '@core/services/creator-custom-value-check.service';
import { EventTrackingService } from '@core/services/event-tracking.service';
import { GroupChangeTrackerService } from '@core/services/group-change-tracker.service';
import { WindowRedirectService } from '@core/services/window-redirect.service';
import { EPlatformFeature } from '@enums/campaign.enum';
import { EEventPlatformFeature, EEventType } from '@enums/event-tracking.enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IBriefingNote, TCampaignApi } from '@pages/campaigns/campaigns.interface';
import { ICreator } from '@pages/creators/creators.interface';
import { BriefingNotesDialogComponent } from '@shared/components/briefing-notes-dialog/briefing-notes-dialog.component';
import {
  IBriefingNotesDialogData,
  IBriefingNotesDialogResult,
} from '@shared/components/briefing-notes-dialog/briefing-notes-dialog.interface';
import { ConfirmDialogComponent } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { IconBadgeComponent } from '@shared/components/icon-badge/icon-badge.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { ALL_CREATORS_ID } from '@shared/constants/all-creators.constants';
import { toEventPlatformFeature } from '@utilities/event-payload-mapping.util';
import { SocialUtilitiesService } from '@utilities/social-utilities.service';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { hasBriefingNoteContent, haveBriefingNotesChanged } from './briefing-note-tracking.util';
import { CampaignFormCreatorsListComponent } from './campaign-form-creators-list/campaign-form-creators-list.component';
import { CreatorAndBudgetDialogComponent } from './creator-and-budget-dialog/creator-and-budget-dialog.component';
import {
  ICreatorAndBudgetDialogData,
  ICreatorAndBudgetDialogResult,
} from './creator-and-budget-dialog/creator-and-budget-dialog.interface';
import { ICreatorsAndCtasValues } from './creators-and-ctas.interface';
import { CreatorsAndCtasService } from './creators-and-ctas.service';
import { CtaTemplateCopyService } from './cta-template-copy.service';
import { DeliverableFlowDialogComponent } from './deliverable-flow-dialog/deliverable-flow-dialog.component';
import {
  EDeliverableFlowStep,
  IDeliverableFlowDialogData,
  IDeliverableFlowDialogResult,
} from './deliverable-flow-dialog/deliverable-flow-dialog.interface';
import { DeliverableStepService } from './deliverable-flow-dialog/deliverable-step/deliverable-step.service';
import { GlobalDeliverablesListComponent } from './global-deliverables-list/global-deliverables-list.component';
import { getGroupCategory, isGlobalGroupFamily, TGroupCategory } from './group-classification.util';

// Captured at dialog-open time for an edit-mode open (Wave 10's group_changed/removed counterpart
// to #10/#11) - the close result carries no before/after diff of its own, same reason
// wasCreateMode is captured this way (see onAddDeliverablesClick's own comment). platformFeature
// was added for ME-4 (audit 2026-08-06) - the platform_feature change event.
interface IGroupSnapshot {
  creatorIds: string[];
  isAllCreators: boolean;
  name: string | null;
  platformFeature: EPlatformFeature | null;
}

@Component({
  imports: [
    TranslateModule,
    IconBadgeComponent,
    MatInputModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    ReactiveFormsModule,
    MatButtonModule,
    NgxMatSelectSearchModule,
    MatTooltipModule,
    GlobalDeliverablesListComponent,
    CampaignFormCreatorsListComponent,
    IconComponent,
    MatMenuModule,
  ],
  selector: 'app-creators-and-ctas',
  styleUrls: ['./creators-and-ctas.component.scss'],
  templateUrl: './creators-and-ctas.component.html',
})
export class CreatorsAndCtasComponent {
  public readonly campaign = input.required<TCampaignApi | null>();
  public readonly getValues = input.required<boolean>();

  public readonly canSave = output<boolean>();
  public readonly changed = output<boolean>();
  public readonly values = output<ICreatorsAndCtasValues>();

  public readonly globalAndSharedCtaValues = signal<FormGroup<ICtaForm>['value'][]>([]);
  public readonly ctasShareCountMap = signal<Map<string, number>>(new Map());
  public readonly creatorHasCustomValueMap = signal<Map<string, boolean>>(new Map());
  public readonly newlyAddedCreatorIds = signal<string[]>([]);

  public readonly ALL_CREATORS_ID = ALL_CREATORS_ID;
  public readonly EDeliverableFlowStep = EDeliverableFlowStep;
  public readonly getExistingDeliverablesCountForGlobal = (ctaId: string, creatorId: string): number => {
    return this.getExistingDeliverablesCount(ctaId, creatorId);
  };
  public readonly getExistingExecutionsCountForGlobal = (ctaId: string, creatorId: string): number | null => {
    return this.getExistingExecutionsCount(ctaId, creatorId);
  };
  public readonly getExistingKpisCountForGlobal = (ctaId: string, creatorId: string): number => {
    return this.getExistingKpisCount(ctaId, creatorId);
  };

  public readonly ctasValueMap = new Map<string, FormGroup<ICtaForm>['value']>();

  public campaignCreatorsValue: FormGroup<ICampaignCreatorForm>['value'][] = [];
  public ctaTemplates: ICtaTemplateApi[] = [];
  public readonly briefingNotes = signal<IBriefingNote[]>([]);

  public readonly hasGlobalBriefingNote = computed(() => {
    const note = this.briefingNotes().find((n) => n.creatorId === null);

    return !!note && (!!note.note?.trim() || !!note.files?.length);
  });

  private readonly deliverablesCountCache = new Map<string, number>();
  private readonly executionsCountCache = new Map<string, number>();
  private readonly kpisCountCache = new Map<string, number>();

  private readonly creatorsAndCtasService = inject(CreatorsAndCtasService);
  private readonly ctaTemplateCopyService = inject(CtaTemplateCopyService);
  private readonly deliverableStepService = inject(DeliverableStepService);
  private readonly dialog = inject(MatDialog);
  private readonly creatorCustomValueCheckService = inject(CreatorCustomValueCheckService);
  private readonly translate = inject(TranslateService);
  private readonly eventTrackingService = inject(EventTrackingService);
  private readonly groupChangeTracker = inject(GroupChangeTrackerService);

  private changedOnce = false;

  constructor() {
    this.createInitializeEffect();
    this.createGetValuesEffect();
  }

  public removeCreatorClicked(creatorToRemove?: ICreator | null): void {
    if (!creatorToRemove) {
      return;
    }

    const campaignCreatorIndex = this.campaignCreatorsValue.findIndex(
      (creator) => creator.creatorId === creatorToRemove.id,
    );

    if (campaignCreatorIndex === undefined || campaignCreatorIndex === -1) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        cancelText: this.translate.instant('pages.campaignForm.sections.creatorsAndCtas.removeCreator.cancelText'),
        confirmText: this.translate.instant('pages.campaignForm.sections.creatorsAndCtas.removeCreator.confirmText'),
        message: this.translate.instant('pages.campaignForm.sections.creatorsAndCtas.removeCreator.message', {
          name: creatorToRemove.name,
        }),
        title: this.translate.instant('pages.campaignForm.sections.creatorsAndCtas.removeCreator.title'),
      },
    });

    dialogRef.afterClosed().subscribe((result: boolean | undefined) => {
      if (result) {
        this.removeCreator(campaignCreatorIndex);
      }
    });
  }

  public editCreator(creatorToEdit?: ICreator | null): void {
    if (!creatorToEdit) {
      return;
    }

    this.onAddCreatorClick(creatorToEdit.id);
  }

  public getExistingDeliverablesCount(ctaId: string, creatorId: string): number {
    const cacheKey = `${ctaId}_${creatorId}`;

    return this.deliverablesCountCache.get(cacheKey) ?? 0;
  }

  public getExistingExecutionsCount(ctaId: string, creatorId: string): number | null {
    const cacheKey = `${ctaId}_${creatorId}`;

    return this.executionsCountCache.get(cacheKey) ?? null;
  }

  public getExistingKpisCount(ctaId: string, creatorId: string): number {
    const cacheKey = `${ctaId}_${creatorId}`;

    return this.kpisCountCache.get(cacheKey) ?? 0;
  }

  public onAddCreatorClick(creatorToEditId?: string): void {
    const data: ICreatorAndBudgetDialogData = {
      campaign: this.campaign()!,
      campaignCreatorsValue: this.campaignCreatorsValue,
    };

    if (creatorToEditId) {
      data.creatorToEditId = creatorToEditId;
    }

    const dialogRef = this.dialog.open(CreatorAndBudgetDialogComponent, {
      data,
      disableClose: true,
      maxWidth: 'calc(100vw - 32px)',
      width: '760px',
    });

    dialogRef.afterClosed().subscribe((result: ICreatorAndBudgetDialogResult | undefined) => {
      if (result?.saved) {
        // Create new element references so signal inputs in child components re-evaluate
        this.campaignCreatorsValue = this.campaignCreatorsValue.map((c) => ({ ...c }));
        this.emitChanged(true);
      }

      if (result?.continueWithDeliverables && result.addedCreatorIds?.length) {
        this.onAddDeliverablesClick(result.addedCreatorIds);
      }

      if (result?.addedCreatorIds?.length) {
        this.newlyAddedCreatorIds.set(result.addedCreatorIds);
        this.creatorsAndCtasService.addGlobalCtasToNewCreators(
          this.campaignCreatorsValue,
          result.addedCreatorIds,
          this.globalAndSharedCtaValues(),
        );
        this.updateUI();
        setTimeout(() => this.newlyAddedCreatorIds.set([]));
      }
    });
  }

  public onAddDeliverablesClick(
    creatorIds: string[] | null,
    ctaId?: string | null,
    startingStep?: EDeliverableFlowStep,
    creatorId?: string | null,
  ): void {
    if (!creatorIds?.length) {
      return;
    }

    const data: IDeliverableFlowDialogData = {
      campaignCreatorsValue: this.campaignCreatorsValue,
      createMode: true,
      creatorIds,
      ctasValues: Array.from(this.ctasValueMap.values()),
      customModeInitialCreatorId: creatorId ?? null,
    };

    let previousGroupSnapshot: IGroupSnapshot | undefined;

    if (ctaId) {
      const ctaCreatorIds = this.campaignCreatorsValue.reduce((acc, campaignCreator) => {
        if (campaignCreator.ctas?.some((cta) => cta.id === ctaId)) {
          acc.push(campaignCreator.creatorId!);
        }

        return acc;
      }, [] as string[]);

      data.startingStep = startingStep ?? EDeliverableFlowStep.deliverable;
      data.createMode = false;
      data.ctaValues = this.ctasValueMap.get(ctaId);
      data.creatorIds = ctaCreatorIds;

      if (data.ctaValues?.isAllCreators) {
        data.creatorIds.push(ALL_CREATORS_ID);
      }

      const isIndividualCta = !data.ctaValues?.isAllCreators && ctaCreatorIds.length <= 1;

      if (isIndividualCta) {
        data.customModeInitialCreatorId = null;
      }

      previousGroupSnapshot = {
        creatorIds: ctaCreatorIds,
        isAllCreators: !!data.ctaValues?.isAllCreators,
        name: data.ctaValues?.name ?? null,
        platformFeature: data.ctaValues?.platformFeature ?? null,
      };
    }

    const dialogRef = this.dialog.open(DeliverableFlowDialogComponent, {
      data,
      disableClose: true,
      maxHeight: '90vh',
      width: '840px',
    });

    // #10/#11 only fire for a genuine new group (not editing an existing CTA) - createMode is only
    // known here at open time (the close result carries no new-vs-edited flag), so capture it now
    // and carry it into the close handler. previousGroupSnapshot is the edit-mode counterpart, for
    // group_changed/removed (Wave 10).
    this.createDeliverableDialogCloseSubscription(
      dialogRef,
      data.createMode ?? false,
      creatorIds[0] !== ALL_CREATORS_ID ? creatorIds[0] : undefined,
      previousGroupSnapshot,
    );
  }

  public removeCtaFromCreator(creatorId: string, ctaId: string, disabled = false): void {
    if (disabled) {
      return;
    }

    const campaignCreator = this.campaignCreatorsValue.find((creator) => creator.creatorId === creatorId);
    const ctaIndex = campaignCreator?.ctas?.findIndex((cta) => cta.id === ctaId);

    if (ctaIndex === undefined || ctaIndex === -1) {
      return;
    }

    const ctaValues = this.ctasValueMap.get(ctaId);
    const previousCreatorIds = this.getCtaCreatorIds(ctaId);

    campaignCreator?.ctas?.splice(ctaIndex, 1);

    const newCreatorIds = this.getCtaCreatorIds(ctaId);

    // Only delete once no creator references it anymore - deleting it unconditionally (regardless
    // of whether this was a shared group still assigned to other creators) used to orphan those
    // other creators' own references to the same id. Found and fixed during Wave 10 planning, as a
    // prerequisite for tracking this removal correctly.
    if (newCreatorIds.length === 0) {
      this.ctasValueMap.delete(ctaId);
    }

    this.updateUI();
    this.trackGroupRemoval(ctaValues, previousCreatorIds, newCreatorIds, creatorId);
  }

  public copyCta(ctaId: string): void {
    const creatorIds: string[] = this.getCreatorIdsForCta(ctaId);

    this.copyCtaToCreators(ctaId, creatorIds);
  }

  public copyCtaForCreator(ctaId: string, creatorId: string): void {
    this.copyCtaToCreators(ctaId, [creatorId], false);
  }

  public removeCtaGlobally(ctaId: string): void {
    const ctaValues = this.ctasValueMap.get(ctaId);
    const previousCreatorIds = this.getCtaCreatorIds(ctaId);

    this.campaignCreatorsValue.forEach((creator) => {
      const ctaIndex = creator.ctas?.findIndex((cta) => cta.id === ctaId);

      if (ctaIndex === undefined || ctaIndex === -1) {
        return;
      }

      creator.ctas?.splice(ctaIndex, 1);
    });

    this.ctasValueMap.delete(ctaId);
    this.updateUI();
    this.trackGroupRemoval(ctaValues, previousCreatorIds, [], undefined);
  }

  public openCreatorDetails(creator: ICreator): void {
    WindowRedirectService.openUrlInNewTab(`/creators/${creator.id}`);
  }

  public editCreatorById(creatorId: string): void {
    const creator = this.campaignCreatorsValue.find((c) => c.creatorId === creatorId)?.creator;

    this.editCreator(creator);
  }

  public removeCreatorById(creatorId: string): void {
    const creator = this.campaignCreatorsValue.find((c) => c.creatorId === creatorId)?.creator;

    this.removeCreatorClicked(creator);
  }

  public openCreatorBriefingNotesById(creatorId: string): void {
    const creator = this.campaignCreatorsValue.find((c) => c.creatorId === creatorId)?.creator;

    this.onOpenCreatorBriefingNotes(creator);
  }

  public onOpenCreatorBriefingNotes(creator: ICreator | null | undefined): void {
    if (!creator?.id) {
      return;
    }

    const campaignCreator = this.campaignCreatorsValue.find(
      (campaignCreatorValue) => campaignCreatorValue.creatorId === creator.id,
    );

    if (!campaignCreator?.id) {
      return;
    }

    this.openBriefingNotesDialog(creator.id, creator.name ?? null);
  }

  public onOpenGlobalBriefingNotes(): void {
    this.openBriefingNotesDialog(null, null);
  }

  private createInitializeEffect(): void {
    effect(() => {
      const campaign = this.campaign();

      if (!campaign) {
        return;
      }

      this.initialize();
    });
  }

  private initialize(): void {
    this.resetUI();
    this.initializeCtasValue();
    this.initializeCampaignCreatorsValue();
    this.updateUI(false);
  }

  private initializeCtasValue(): void {
    const { ctaTemplateForms, ctaTemplates } = this.creatorsAndCtasService.createCtaTemplateFormsFromCampaign(
      this.campaign() ?? null,
      false,
    );

    ctaTemplateForms.forEach((ctaForm, ctaId) => {
      this.ctasValueMap.set(ctaId, ctaForm.value);
    });

    this.ctaTemplates = this.creatorsAndCtasService.sortCtaTemplates(ctaTemplates);
  }

  private initializeCampaignCreatorsValue(): void {
    this.campaignCreatorsValue =
      this.campaign()?.campaignCreators?.map((campaignCreator) => ({
        campaignId: campaignCreator.campaignId,
        cost: campaignCreator.cost,
        creator: campaignCreator.creator,
        creatorId: campaignCreator.creatorId,
        ctas: this.ctaTemplates.reduce(
          (acc, ctaTemplate) => {
            if (
              ctaTemplate.ctaCreators?.some((ctaCreator) => ctaCreator.creatorId === campaignCreator.creatorId) ||
              (ctaTemplate.isAllCreators &&
                SocialUtilitiesService.isPlatformCompatible(
                  ctaTemplate.platformFeature,
                  undefined,
                  campaignCreator.creator?.channels,
                ))
            ) {
              acc.push(this.ctasValueMap.get(ctaTemplate.id)!);
            }

            return acc;
          },
          [] as FormArray<FormGroup<ICtaForm>>['value'],
        ),
        id: campaignCreator.id,
      })) ?? [];

    this.campaignCreatorsValue.sort((a, b) => {
      const nameA = a.creator?.name || '';
      const nameB = b.creator?.name || '';

      return nameA.localeCompare(nameB);
    });

    this.briefingNotes.set([...(this.campaign()?.briefingNotes ?? [])]);
  }

  private updateGlobalAndSharedCtaValues(): void {
    const [globalAndSharedCtas, ctaUsageCount] = this.creatorsAndCtasService.getGlobalAndSharedCtas(
      this.campaignCreatorsValue,
      Array.from(this.ctasValueMap.values()),
    );
    this.globalAndSharedCtaValues.set(globalAndSharedCtas);
    this.ctasShareCountMap.set(ctaUsageCount);
  }

  private updateExecutionDetailsCache(): void {
    this.creatorsAndCtasService.updateCtaInformationCache(
      this.campaignCreatorsValue,
      this.deliverablesCountCache,
      this.executionsCountCache,
      this.kpisCountCache,
      untracked(this.globalAndSharedCtaValues),
    );

    this.canSave.emit(
      this.creatorsAndCtasService.canSaveCreatorAndCtas(
        this.campaignCreatorsValue,
        this.deliverablesCountCache,
        this.executionsCountCache,
        untracked(this.globalAndSharedCtaValues),
      ),
    );
  }

  private createDeliverableDialogCloseSubscription(
    dialogRef: MatDialogRef<DeliverableFlowDialogComponent>,
    wasCreateMode: boolean,
    openCreatorId: string | undefined,
    previousGroupSnapshot?: IGroupSnapshot,
  ): void {
    dialogRef.afterClosed().subscribe((result: IDeliverableFlowDialogResult | undefined) => {
      if (result?.ctaFormValues) {
        this.ctasValueMap.set(result.ctaFormValues.id!, result.ctaFormValues);

        if (wasCreateMode && !result.discarded) {
          this.trackGroupAdded(result.ctaFormValues, openCreatorId);
        } else if (!wasCreateMode && !result.discarded && previousGroupSnapshot) {
          this.trackGroupChange(result.ctaFormValues, previousGroupSnapshot, openCreatorId);
        }
      }

      if (result?.newPlatformAddedCreatorIds?.length) {
        this.creatorsAndCtasService.addGlobalCtasToNewCreators(
          this.campaignCreatorsValue,
          result.newPlatformAddedCreatorIds,
          this.globalAndSharedCtaValues(),
        );
      }

      this.updateUI(result?.discarded ? this.changedOnce : true);

      // Spread to new array reference so signal inputs in child components detect the change.
      // Only needed when data was actually modified (not on discard).
      if (!result?.discarded) {
        this.campaignCreatorsValue = this.campaignCreatorsValue.map((c) => ({ ...c }));
      }
    });
  }

  private updateCreatorHasCustomValueMap(): void {
    this.creatorHasCustomValueMap.set(
      this.creatorCustomValueCheckService.calculateCreatorsCustomValues(
        this.campaignCreatorsValue.map((creator) => creator.creatorId!) ?? [],
        undefined,
        this.campaignCreatorsValue,
      ),
    );
  }

  private updateUI(changed = true): void {
    this.emitChanged(changed);
    this.updateGlobalAndSharedCtaValues();
    this.updateExecutionDetailsCache();
    this.updateCreatorHasCustomValueMap();
  }

  private resetUI(): void {
    this.globalAndSharedCtaValues.set([]);
    this.ctasShareCountMap.set(new Map());
    this.creatorHasCustomValueMap.set(new Map());
    this.campaignCreatorsValue = [];
    this.ctaTemplates = [];
    this.deliverablesCountCache.clear();
    this.executionsCountCache.clear();
    this.kpisCountCache.clear();
    this.ctasValueMap.clear();
    this.briefingNotes.set([]);
  }

  private createGetValuesEffect(): void {
    effect(() => {
      if (this.getValues()) {
        this.values.emit({
          briefingNotes: this.briefingNotes(),
          campaignCreatorsValue: this.campaignCreatorsValue,
          ctasValue: Array.from(this.ctasValueMap.values()),
        });
      }
    });
  }

  private openBriefingNotesDialog(creatorId: string | null, creatorName: string | null): void {
    const briefingNote =
      this.briefingNotes().find((note) => note.creatorId === creatorId) ??
      this.campaign()?.briefingNotes?.find((note) => note.creatorId === creatorId) ??
      null;
    // Captured at open time, not derivable from the close result - upsertBriefingNote() can't tell
    // create from edit itself. Wave 11 - keyed off actual content (matches
    // campaign-form-payload.service.ts's getBriefingNotes() persistence gate), not mere presence of
    // a briefingNote object, so opening+saving an empty dialog doesn't fire "added".
    const hadNoteContent = hasBriefingNoteContent(briefingNote);
    const data: IBriefingNotesDialogData = {
      briefingNote,
      campaignId: this.campaign()?.id ?? '',
      creatorId: briefingNote?.creatorId ?? creatorId,
      creatorName,
      viewMode: false,
    };
    const dialogRef = this.dialog.open(BriefingNotesDialogComponent, {
      data,
      disableClose: true,
      width: '700px',
    });

    dialogRef.afterClosed().subscribe((result: IBriefingNotesDialogResult | undefined) => {
      if (!result?.briefingNote) {
        return;
      }

      this.upsertBriefingNote(result.briefingNote);
      this.emitChanged(true);
      this.trackBriefingNoteChange(creatorId, briefingNote, result.briefingNote, hadNoteContent);
    });
  }

  // Wave 11 - added/changed/removed are mutually exclusive transitions of "does a note with real
  // content exist for this creatorId (or globally, creatorId === null)". Clearing a note back to
  // empty fires removed instead of changed; saving with no real content both before and after (the
  // dialog has no save-disabled guard for emptiness) fires nothing, matching the "never fabricate an
  // event" rule used everywhere else in this system.
  private trackBriefingNoteChange(
    creatorId: string | null,
    previousNote: IBriefingNote | null,
    newNote: IBriefingNote,
    hadNoteContent: boolean,
  ): void {
    const hasNoteContentNow = hasBriefingNoteContent(newNote);

    if (!hadNoteContent && !hasNoteContentNow) {
      return;
    }

    const isGlobal = creatorId === null;
    const properties = { campaignId: this.campaign()?.id, creatorId: creatorId ?? undefined };

    if (!hadNoteContent) {
      this.eventTrackingService.track(
        isGlobal
          ? EEventType.campaignCreationGlobalBriefingNotesAdded
          : EEventType.campaignCreationCustomBriefingNotesAdded,
        properties,
      );

      return;
    }

    if (!hasNoteContentNow) {
      this.eventTrackingService.track(
        isGlobal
          ? EEventType.campaignCreationGlobalBriefingNotesRemoved
          : EEventType.campaignCreationCustomBriefingNotesRemoved,
        properties,
      );

      return;
    }

    if (haveBriefingNotesChanged(previousNote, newNote)) {
      this.eventTrackingService.track(
        isGlobal
          ? EEventType.campaignCreationGlobalBriefingNotesChanged
          : EEventType.campaignCreationCustomBriefingNotesChanged,
        properties,
      );
    }
  }

  private upsertBriefingNote(briefingNote: IBriefingNote): void {
    const existingIndex = this.briefingNotes().findIndex(
      (existingNote) => existingNote.creatorId === briefingNote.creatorId,
    );

    if (existingIndex === -1) {
      this.briefingNotes.set([...this.briefingNotes(), briefingNote]);

      return;
    }

    const updatedBriefingNotes = [...this.briefingNotes()];
    updatedBriefingNotes[existingIndex] = briefingNote;
    this.briefingNotes.set(updatedBriefingNotes);
  }

  private emitChanged(changed: boolean): void {
    if (changed) {
      this.changedOnce = true;
    }

    this.changed.emit(changed);
  }

  private removeCreator(creatorToRemoveIndex: number): void {
    // New array reference so signal inputs in child components (e.g. the creators count) re-evaluate.
    this.campaignCreatorsValue = this.campaignCreatorsValue.filter((_, index) => index !== creatorToRemoveIndex);
    this.updateUI();
  }

  private getCreatorIdsForCta(ctaId: string): string[] {
    return this.campaignCreatorsValue.reduce((creatorIds, campaignCreator) => {
      if (!campaignCreator.ctas?.some((cta) => cta.id === ctaId)) {
        return creatorIds;
      }

      creatorIds.push(campaignCreator.creatorId!);

      return creatorIds;
    }, [] as string[]);
  }

  // Wave 10 - how many creators currently reference this group, used by getGroupCategory() to
  // decide custom vs shared vs global. Counts campaignCreatorsValue directly rather than trusting
  // ctaShareCountMap, since that signal is only recomputed by updateUI() and callers here need the
  // count both before and after a mutation within the same synchronous call.
  private getCtaCreatorIds(ctaId: string): string[] {
    return this.campaignCreatorsValue.reduce((acc, creator) => {
      if (creator.ctas?.some((cta) => cta.id === ctaId) && creator.creatorId) {
        acc.push(creator.creatorId);
      }

      return acc;
    }, [] as string[]);
  }

  // 'global' never needs a creatorId (belongs to everyone); 'custom' has exactly one creator, so
  // creatorIds[0] IS that creator (fallbackCreatorId only covers the rare case creatorIds can't be
  // derived live, e.g. before the dialog's mutation has landed); 'shared' has no single owner, so
  // every assigned creator's id is reported, comma-separated.
  private getGroupEventCreatorId(
    category: TGroupCategory,
    creatorIds: string[],
    fallbackCreatorId?: string,
  ): string | undefined {
    if (category === 'global') {
      return undefined;
    }

    // Sorted so the same creator set always joins into the same string regardless of which order
    // the caller happened to collect it in - campaign-form.component.ts has its own copy of this
    // helper for _saved events, built from a different source (backend ctaCreators vs. live
    // campaignCreatorsValue), so without a shared sort key the two could report the same set of
    // creators in a different order.
    return category === 'shared' ? [...creatorIds].sort().join(',') : (creatorIds[0] ?? fallbackCreatorId);
  }

  // Shared by trackGroupAdded and the flip branches of trackGroupChange/trackGroupRemoval - a
  // global_group_added event's newValue now reports which of the two "global family" categories
  // this actually is ('shared' or true 'global'), per product decision; custom_group_added never
  // sets newValue (only one custom sub-category exists). CTR-1 (audit 2026-08-06) - group-scoped
  // counts on specific events: the group's creator reach + its own deliverables count.
  private trackGroupAddedEvent(
    ctaId: string,
    category: TGroupCategory,
    creatorIds: string[],
    options: {
      campaignCreationCreatorCounter?: number;
      deliverableGroupCounter?: number;
      platformFeature?: EEventPlatformFeature;
      fallbackCreatorId?: string;
    },
  ): void {
    const isGlobalFamily = isGlobalGroupFamily(category);

    this.eventTrackingService.track(
      isGlobalFamily ? EEventType.campaignCreationGlobalGroupAdded : EEventType.campaignCreationCustomGroupAdded,
      {
        campaignCreationCreatorCounter: options.campaignCreationCreatorCounter,
        campaignCreationDeliverableGroupCounter: options.deliverableGroupCounter,
        campaignId: this.campaign()?.id,
        creatorId: this.getGroupEventCreatorId(category, creatorIds, options.fallbackCreatorId),
        ctaTemplateId: ctaId,
        newValue: isGlobalFamily ? category : undefined,
        platformFeature: options.platformFeature,
      },
    );
  }

  private trackGroupAdded(ctaFormValues: FormGroup<ICtaForm>['value'], openCreatorId: string | undefined): void {
    const ctaId = ctaFormValues.id!;
    const creatorIds = this.getCtaCreatorIds(ctaId);
    const category = getGroupCategory(ctaFormValues.isAllCreators, creatorIds.length);

    this.trackGroupAddedEvent(ctaId, category, creatorIds, {
      campaignCreationCreatorCounter: creatorIds.length,
      deliverableGroupCounter: ctaFormValues.deliverables?.length,
      fallbackCreatorId: openCreatorId,
      platformFeature: ctaFormValues.platformFeature
        ? toEventPlatformFeature(ctaFormValues.platformFeature)
        : undefined,
    });
  }

  // Wave 10 - editing an existing group (reopening the dialog in edit mode) previously fired no
  // event at all. Diffs the edit-mode close result against the snapshot captured at dialog-open
  // time (previousGroupSnapshot, from onAddDeliverablesClick). A category-family flip (custom <->
  // shared/global) fires removed(old) + added(new) - the removed event carries the literal
  // previous/new category strings ('custom'/'shared'/'global'), the added event doesn't (matching
  // #10/#11's existing shape, aside from newValue on a global_group_added - see
  // trackGroupAddedEvent). Staying in the same family but with a different name or assigned-creator
  // count fires changed instead, per-field (source: 'name' / 'cta_creators').
  private trackGroupChange(
    ctaFormValues: FormGroup<ICtaForm>['value'],
    previous: IGroupSnapshot,
    openCreatorId: string | undefined,
  ): void {
    const ctaId = ctaFormValues.id!;
    const newCreatorIds = this.getCtaCreatorIds(ctaId);
    const previousCategory = getGroupCategory(previous.isAllCreators, previous.creatorIds.length);
    const newCategory = getGroupCategory(ctaFormValues.isAllCreators, newCreatorIds.length);
    const wasGlobalFamily = isGlobalGroupFamily(previousCategory);
    const isGlobalFamilyNow = isGlobalGroupFamily(newCategory);
    const platformFeature = ctaFormValues.platformFeature
      ? toEventPlatformFeature(ctaFormValues.platformFeature)
      : undefined;

    if (isGlobalFamilyNow !== wasGlobalFamily) {
      this.eventTrackingService.track(
        wasGlobalFamily ? EEventType.campaignCreationGlobalGroupRemoved : EEventType.campaignCreationCustomGroupRemoved,
        {
          campaignCreationCreatorCounter: previous.creatorIds.length,
          campaignCreationDeliverableGroupCounter: ctaFormValues.deliverables?.length,
          campaignId: this.campaign()?.id,
          creatorId: this.getGroupEventCreatorId(previousCategory, previous.creatorIds, openCreatorId),
          ctaTemplateId: ctaId,
          newValue: newCategory,
          platformFeature,
          previousValue: previousCategory,
        },
      );

      this.trackGroupAddedEvent(ctaId, newCategory, newCreatorIds, {
        campaignCreationCreatorCounter: newCreatorIds.length,
        deliverableGroupCounter: ctaFormValues.deliverables?.length,
        platformFeature,
      });

      return;
    }

    const changedEventType = isGlobalFamilyNow
      ? EEventType.campaignCreationGlobalGroupChanged
      : EEventType.campaignCreationCustomGroupChanged;
    const creatorId = this.getGroupEventCreatorId(newCategory, newCreatorIds, openCreatorId);

    this.trackGroupSameFamilyChanges(ctaId, changedEventType, {
      creatorId,
      current: {
        creatorCount: newCreatorIds.length,
        isAllCreators: !!ctaFormValues.isAllCreators,
        name: ctaFormValues.name ?? null,
        platformFeature: ctaFormValues.platformFeature ?? null,
      },
      deliverableGroupCounter: ctaFormValues.deliverables?.length,
      newCreatorCount: newCreatorIds.length,
      previous: {
        creatorCount: previous.creatorIds.length,
        isAllCreators: previous.isAllCreators,
        name: previous.name,
        platformFeature: previous.platformFeature,
      },
    });
  }

  // Wave 10/ME-4 - the same-family (no custom<->shared/global flip) changed branches of
  // trackGroupChange: one _changed event per genuinely changed field (name, platform feature,
  // creator reach), each marked into GroupChangeTrackerService for the _saved counterpart.
  // Extracted to keep trackGroupChange under the project's max-lines-per-function limit.
  private trackGroupSameFamilyChanges(
    ctaId: string,
    changedEventType: EEventType,
    options: {
      creatorId: string | undefined;
      deliverableGroupCounter: number | undefined;
      newCreatorCount: number;
      previous: {
        creatorCount: number;
        isAllCreators: boolean;
        name: string | null;
        platformFeature: EPlatformFeature | null;
      };
      current: {
        creatorCount: number;
        isAllCreators: boolean;
        name: string | null;
        platformFeature: EPlatformFeature | null;
      };
    },
  ): void {
    const { creatorId, current, deliverableGroupCounter, newCreatorCount, previous } = options;
    // Round-3 audit - every group_* event carries platformFeature.
    const platformFeature = current.platformFeature ? toEventPlatformFeature(current.platformFeature) : undefined;

    if (previous.name !== current.name) {
      this.groupChangeTracker.markFieldChanged(ctaId, 'name');
      this.eventTrackingService.track(changedEventType, {
        campaignCreationCreatorCounter: newCreatorCount,
        campaignCreationDeliverableGroupCounter: deliverableGroupCounter,
        campaignId: this.campaign()?.id,
        creatorId,
        ctaTemplateId: ctaId,
        newValue: current.name ?? undefined,
        platformFeature,
        previousValue: previous.name ?? undefined,
        source: 'name',
      });
    }

    // ME-4 (audit 2026-08-06) - a real platform change previously fired nothing (global groups)
    // or was misattributed as a reach change (compatible-creator count drift as a side effect).
    // It's a genuine edit with its own source; the cta_creators branch stays suppressed for
    // isAllCreators groups so the side effect doesn't double-report.
    this.trackGroupPlatformFeatureChange(ctaId, changedEventType, {
      creatorId,
      deliverableGroupCounter,
      newCreatorCount,
      newPlatformFeature: current.platformFeature,
      previousPlatformFeature: previous.platformFeature,
    });

    // A truly-global (isAllCreators) group can see its compatible-creator count drift purely from
    // a platformFeature edit (updateCampaignCreatorsCtaValues recomputes the reach) - that's not a
    // user-initiated reach edit, so don't fire cta_creators there. Any other same-family reach
    // change (e.g. shared 2->3, or isAllCreators flipping while staying in the family) is real.
    const isMeaninglessGlobalReachDrift = current.isAllCreators && previous.isAllCreators;

    if (!isMeaninglessGlobalReachDrift && previous.creatorCount !== current.creatorCount) {
      this.groupChangeTracker.markFieldChanged(ctaId, 'cta_creators');
      this.eventTrackingService.track(changedEventType, {
        campaignCreationCreatorCounter: newCreatorCount,
        campaignCreationDeliverableGroupCounter: deliverableGroupCounter,
        campaignId: this.campaign()?.id,
        creatorId,
        ctaTemplateId: ctaId,
        newValue: String(current.creatorCount),
        platformFeature,
        previousValue: String(previous.creatorCount),
        source: 'cta_creators',
      });
    }
  }

  // ME-4 - the platform_feature changed branch (see trackGroupSameFamilyChanges's comment).
  private trackGroupPlatformFeatureChange(
    ctaId: string,
    changedEventType: EEventType,
    values: {
      creatorId: string | undefined;
      deliverableGroupCounter: number | undefined;
      newCreatorCount: number;
      newPlatformFeature: EPlatformFeature | null;
      previousPlatformFeature: EPlatformFeature | null;
    },
  ): void {
    const { creatorId, deliverableGroupCounter, newCreatorCount } = values;

    if (values.previousPlatformFeature === values.newPlatformFeature) {
      return;
    }

    this.groupChangeTracker.markFieldChanged(ctaId, 'platform_feature');
    this.eventTrackingService.track(changedEventType, {
      campaignCreationCreatorCounter: newCreatorCount,
      campaignCreationDeliverableGroupCounter: deliverableGroupCounter,
      campaignId: this.campaign()?.id,
      creatorId,
      ctaTemplateId: ctaId,
      newValue: values.newPlatformFeature ?? undefined,
      // Round-3 audit - every group_* event carries platformFeature (the resulting one).
      platformFeature: values.newPlatformFeature ? toEventPlatformFeature(values.newPlatformFeature) : undefined,
      previousValue: values.previousPlatformFeature ?? undefined,
      source: 'platform_feature',
    });
  }

  // Wave 10 - shared by removeCtaFromCreator (may shrink a shared group's reach, or fully remove a
  // custom one) and removeCtaGlobally (always a full removal, newCreatorIds always empty). A full
  // removal (nothing left referencing this id) fires removed with no new/previous values; a
  // reclassifying removal (still referenced by someone, but crossed the custom<->shared/global
  // threshold) fires removed(old->new category) + added(new), same shape as trackGroupChange's
  // family-flip branch; a same-family shrink (e.g. shared 3->2) fires changed instead.
  private trackGroupRemoval(
    ctaValues: FormGroup<ICtaForm>['value'] | undefined,
    previousCreatorIds: string[],
    newCreatorIds: string[],
    removedFromCreatorId: string | undefined,
  ): void {
    if (!ctaValues?.id) {
      return;
    }

    const platformFeature = ctaValues.platformFeature ? toEventPlatformFeature(ctaValues.platformFeature) : undefined;
    const previousCategory = getGroupCategory(ctaValues.isAllCreators, previousCreatorIds.length);
    const wasGlobalFamily = isGlobalGroupFamily(previousCategory);
    const removedEventType = wasGlobalFamily
      ? EEventType.campaignCreationGlobalGroupRemoved
      : EEventType.campaignCreationCustomGroupRemoved;
    const removedCreatorId = this.getGroupEventCreatorId(previousCategory, previousCreatorIds, removedFromCreatorId);

    if (newCreatorIds.length === 0) {
      this.eventTrackingService.track(removedEventType, {
        campaignCreationCreatorCounter: previousCreatorIds.length,
        campaignCreationDeliverableGroupCounter: ctaValues.deliverables?.length,
        campaignId: this.campaign()?.id,
        creatorId: removedCreatorId,
        ctaTemplateId: ctaValues.id,
        platformFeature,
      });

      return;
    }

    const newCategory = getGroupCategory(ctaValues.isAllCreators, newCreatorIds.length);

    if (isGlobalGroupFamily(newCategory) === wasGlobalFamily) {
      this.groupChangeTracker.markFieldChanged(ctaValues.id, 'cta_creators');
      this.eventTrackingService.track(
        wasGlobalFamily ? EEventType.campaignCreationGlobalGroupChanged : EEventType.campaignCreationCustomGroupChanged,
        {
          campaignCreationCreatorCounter: newCreatorIds.length,
          campaignCreationDeliverableGroupCounter: ctaValues.deliverables?.length,
          campaignId: this.campaign()?.id,
          creatorId: this.getGroupEventCreatorId(newCategory, newCreatorIds),
          ctaTemplateId: ctaValues.id,
          newValue: String(newCreatorIds.length),
          platformFeature,
          previousValue: String(previousCreatorIds.length),
          source: 'cta_creators',
        },
      );

      return;
    }

    this.eventTrackingService.track(removedEventType, {
      campaignCreationCreatorCounter: previousCreatorIds.length,
      campaignCreationDeliverableGroupCounter: ctaValues.deliverables?.length,
      campaignId: this.campaign()?.id,
      creatorId: removedCreatorId,
      ctaTemplateId: ctaValues.id,
      newValue: newCategory,
      platformFeature,
      previousValue: previousCategory,
    });

    this.trackGroupAddedEvent(ctaValues.id, newCategory, newCreatorIds, {
      campaignCreationCreatorCounter: newCreatorIds.length,
      deliverableGroupCounter: ctaValues.deliverables?.length,
      platformFeature,
    });
  }

  private copyCtaToCreators(ctaId: string, creatorIds: string[], isAllCreators?: boolean): void {
    const sourceCtaValues: FormGroup<ICtaForm>['value'] | undefined = this.ctasValueMap.get(ctaId);

    if (!sourceCtaValues?.id) {
      return;
    }

    const sourceCtaForm: FormGroup<ICtaForm> = this.creatorsAndCtasService.createCtaFormFromCtaValues(sourceCtaValues);
    const copiedCtaFormValues: FormGroup<ICtaForm>['value'] = this.ctaTemplateCopyService.getCopiedCtaFormValues(
      sourceCtaForm,
      undefined,
      undefined,
      isAllCreators,
    );
    const copiedCtaForm: FormGroup<ICtaForm> = this.ctaTemplateCopyService.createCtaFormFromValues(copiedCtaFormValues);
    const existingCtaValues: FormGroup<ICtaForm>['value'][] = Array.from(this.ctasValueMap.values());

    this.deliverableStepService.ensureCtaNameIsUnique(copiedCtaForm, existingCtaValues);

    const copiedCtaValues: FormGroup<ICtaForm>['value'] = copiedCtaForm.getRawValue();

    this.ctasValueMap.set(copiedCtaValues.id!, copiedCtaValues);
    // LF-5 (audit 2026-08-06) - a copied CTA's children are copy artifacts; register the copy so
    // the _saved added diff skips them (the copied group's own added_saved covers the copy).
    this.groupChangeTracker.markCtaCopied(copiedCtaValues.id!);
    this.creatorsAndCtasService.updateCampaignCreatorsCtaValues(
      this.campaignCreatorsValue,
      creatorIds,
      copiedCtaValues,
    );
    // Round-3 audit (item 5) - a copy mints a genuinely new group; fire the immediate group_added
    // (the group_removed side was already covered). Category from the copy's isAllCreators +
    // target creators; group-scoped counters + platformFeature per the CTR-1/round-3 rules.
    const copiedCategory = getGroupCategory(copiedCtaValues.isAllCreators, creatorIds.length);

    this.trackGroupAddedEvent(copiedCtaValues.id!, copiedCategory, creatorIds, {
      campaignCreationCreatorCounter: creatorIds.length,
      deliverableGroupCounter: copiedCtaValues.deliverables?.length,
      platformFeature: copiedCtaValues.platformFeature
        ? toEventPlatformFeature(copiedCtaValues.platformFeature)
        : undefined,
    });
    this.updateUI();
  }
}
